/**
 * Documentation shown behind the info icon on the Deco Flow dashboard.
 *
 * The dashboard renders a single pair of charts driven by one set of metric
 * controls, so one text serves both the dashboard header and the flow chart.
 * Moved verbatim out of `MopaDecoFlowComponent.getDocumentation()`.
 */
export const MopaDecoFlowChartDocs = {
    dashboard: `<h2>Definition</h2>
<p><strong>Deco Flow</strong> compares gained decorations (<code>inflow_delta</code>) with first placements (<code>new_placed_delta</code>, <code>placement_number = 1</code>). <strong>Net</strong> is gained minus placed, not a spend balance.</p>

<h2>Metric controls</h2>
<ul>
  <li><strong>Value mode</strong>: Total, Average, Median / Percentile, Flow Records or Players. The selected metric applies independently to gained and placed sides.</li>
  <li><strong>Population</strong> applies only to Average and Median / Percentile. <em>Per DEU</em> (the default) counts players with a positive flow on the relevant side; <em>per DAU</em> uses the same population from <code>dashboard_users_activity</code> for both sides.</li>
  <li><strong>Percentiles</strong> apply only to Median / Percentile. P50, P75, P90 and P99 can be combined; each selection produces its own gained and placed series. Flow is first summed per player and date bucket, then <code>APPROX_QUANTILES</code> is calculated over those player values. For <em>per DAU</em>, a player without flow contributes 0. The KPI uses the lowest selected percentile.</li>
</ul>

<p>All formulas below use gained decorations as the example; placed decorations are calculated the same way.</p>

<h2>Calculation</h2>
<table border="1" cellspacing="0" cellpadding="6">
  <thead>
    <tr bgcolor="#f5f5f5">
      <th align="left">Mode</th>
      <th align="left">Calculation</th>
      <th align="left">Denominator</th>
      <th align="left">Applied-range KPI</th>
    </tr>
  </thead>
  <tbody>
    <tr valign="top">
      <td><strong>Total</strong></td>
      <td><code>SUM(inflow_delta)</code></td>
      <td>None &mdash; an absolute amount, not a ratio.</td>
      <td>Sum of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Flow Records</strong></td>
      <td><code>COUNTIF(inflow_delta &gt; 0)</code> over flow rows</td>
      <td>None &mdash; a row count, not a ratio.</td>
      <td>Sum of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Players</strong></td>
      <td><code>COUNT(DISTINCT player_id)</code> with positive flow for each chart point</td>
      <td>None &mdash; a distinct-player count, not a ratio.</td>
      <td>Sum of daily distinct-player counts. A player is intentionally counted again on another date.</td>
    </tr>
    <tr valign="top">
      <td><strong>Average per DEU</strong></td>
      <td><code>SUM(inflow_delta)</code></td>
      <td>Players with positive flow on that side at that chart point.</td>
      <td>Mean of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Average per DAU</strong></td>
      <td><code>SUM(inflow_delta)</code></td>
      <td>Active players (Date); D0 cohort (Install Day); players reaching the day (Active Day).</td>
      <td>Mean of daily values in the applied date range.</td>
    </tr>
    <tr valign="top">
      <td><strong>Median / Percentile</strong></td>
      <td colspan="2"><code>APPROX_QUANTILES</code> over <code>SUM(inflow_delta)</code> per player and date bucket. It is not a ratio. <em>Per DEU</em> keeps only players with positive flow on that side; <em>per DAU</em> uses all eligible players (D0 on Install Day; players reaching the day on Active Day), with missing flow as 0.</td>
      <td>One percentile over all player/date-bucket values in the range, not a mean of daily percentiles.</td>
    </tr>
  </tbody>
</table>

<h2>Interpretation</h2>
<ul>
  <li><strong>DEU is directional</strong>: a player who only places a decoration contributes to placed, not gained. DAU is not directional: the same population is used for both sides. On cohort axes, per-DAU Average and Median / Percentile use D0 (Install Day) or players reaching the day (Active Day), with missing flow as 0. Cohort splits use their matching DAU; Group / Bucket is presentation only.</li>
  <li><strong>Percentiles</strong> are pre-aggregated per player and date bucket, so one highly active player is not weighted by their number of flow records.</li>
  <li><strong>Breakdowns</strong> follow chart zoom. For Players, values are player-days; for Average and Median / Percentile, they aggregate the displayed daily values. A zoomed breakdown can therefore differ from the range-wide KPI, which pools the complete range.</li>
</ul>

<h2>Filter notes</h2>
<ul>
  <li><strong>Source</strong> is naturally one-sided: acquisition sources affect gained decorations; placement rows use <code>DecoPlaced</code>.</li>
  <li><strong>Payment type</strong> is <code>NULL</code> on placement rows, so Free / Paid filters hide the placed side.</li>
  <li>Deco rarity, size, set ID and inspirations are useful Split and Group dimensions for both sides.</li>
</ul>

<h2>Charts and interactions</h2>
<ul>
  <li><strong>Flow</strong> stacks gained decorations above zero and placed decorations below it. <strong>Net</strong> is gained minus placed.</li>
  <li><strong>KPIs</strong> use a separate range-wide query, independent of chart zoom, Split and Group, and show no period-comparison delta. <strong>Affected players</strong> is 100% for per-DAU Average and Median / Percentile; otherwise it is the share of active players who gained or first placed a decoration.</li>
  <li><strong>Breakdowns</strong> rank entries by value and share. Small entries are folded into <strong>Remaining</strong>; click its caret to reveal them. Click a row or chart legend to toggle that series, or use <strong>ALL</strong>/<strong>NONE</strong> for one side.</li>
  <li><strong>Group</strong> buckets a supported dimension in the chart and breakdown.</li>
</ul>

<h2>Lifetime-day axes</h2>
<p><strong>X axis</strong> switches the charts between the loaded date range and a <strong>cohort</strong> view.</p>
<ul>
  <li><strong>Install Day</strong> counts calendar days since first active day. <strong>Active Day</strong> counts only days on which a player was active (the first active day is 0).</li>
  <li><strong>The cohort</strong> is the players whose first open falls in the <strong>First Open Date</strong> range. On both lifetime axes <strong>Activity Date</strong> and app-version filters do not apply.</li>
  <li><strong>Accumulation</strong> switches between daily values and their running sum from day 0. KPI tiles retain their existing date-range calculation in this first version.</li>
</ul>

<h2>Data notes</h2>
<p>Lower the <strong>grouping threshold</strong> to reveal more series. The lifetime axes use <code>player_day.install_day_no</code> or <code>player_day.active_day_no</code>, never deprecated <code>day_no</code>. Both sides read <code>bi_viz.dashboard_deco_activity</code>; per-DAU metrics also read <code>bi_viz.dashboard_users_activity</code>. Both axes are capped at day 90.</p>
`,
} as const;
